import { createI18n } from 'vue-i18n'
import type { Language } from './types'

const zh = {
  app: {
    name: '售后服务管理',
    shortName: '售后管理',
    tagline: '工单、备件与库存协同平台',
    loading: '正在加载',
    save: '保存',
    saving: '保存中…',
    cancel: '取消',
    confirm: '确认',
    close: '关闭',
    edit: '编辑',
    delete: '删除',
    back: '返回',
    search: '搜索',
    reset: '重置',
    add: '新增',
    create: '创建',
    submit: '提交',
    view: '查看',
    actions: '操作',
    all: '全部',
    none: '暂无',
    total: '共 {count} 条',
    page: '第 {page} / {pages} 页',
    previous: '上一页',
    next: '下一页',
    required: '必填',
    optional: '选填',
    success: '操作成功',
    error: '操作失败',
    networkError: '网络连接失败，请稍后重试',
    unsaved: '有尚未保存的更改',
    upload: '上传文件',
    downloadTemplate: '下载模板',
    selectFile: '选择文件',
    dragFile: '拖放文件到此处，或点击选择',
    noResults: '没有找到匹配结果',
    clearSelection: '清除选择',
  },
  nav: {
    orders: '工单',
    orderList: '工单列表',
    createOrder: '创建工单',
    partsOrders: '备件订单',
    warehouse: '备件库存',
    account: '账号与用户',
    process: '工单配置',
    data: '资料管理',
    collapse: '收起侧栏',
    expand: '展开侧栏',
    openMenu: '打开导航',
    logout: '退出登录',
  },
  auth: {
    title: '欢迎回来',
    subtitle: '登录后继续处理售后工单与备件',
    username: '用户名',
    password: '密码',
    usernamePlaceholder: '请输入用户名',
    passwordPlaceholder: '请输入密码',
    signIn: '登录',
    signingIn: '正在登录…',
    required: '请输入用户名和密码',
    failed: '用户名或密码错误',
  },
  role: {
    admin: '管理员',
    repair_station: '维修站',
    brazil: '巴西服务商',
    naitteng: '耐腾',
  },
  order: {
    listTitle: '工单列表',
    listSubtitle: '查看、筛选和跟进全部可访问工单',
    createTitle: '创建工单',
    createSubtitle: '记录车辆、故障、附件和所需备件',
    detailTitle: '工单详情',
    new: '新建工单',
    keyword: '工单号、VIN 或标题',
    status: '状态',
    priority: '优先级',
    orderNumber: '工单号',
    vin: 'VIN / 车架号',
    title: '工单标题',
    faultType: '故障类型',
    creator: '创建人',
    createdAt: '创建时间',
    noOrders: '暂无工单',
    noOrdersHint: '创建第一张工单，开始记录售后问题。',
    basicInfo: '基本信息',
    mileage: '里程',
    mileagePlaceholder: '例如：12000 km',
    faultInfo: '故障信息',
    chooseFault: '请选择故障类型',
    description: '故障描述',
    descriptionPlaceholder: '描述故障现象、发生条件及已做检查',
    remark: '备注',
    remarkPlaceholder: '补充说明（选填）',
    attachments: '图片与视频',
    attachmentsHint: '支持图片和视频；单个文件请控制在合理大小',
    parts: '备件清单',
    addPart: '添加备件',
    partSearch: '输入料号或名称',
    quantity: '数量',
    createSuccess: '工单 {number} 创建成功',
    createFailed: '创建工单失败',
    requiredHint: '请填写工单标题和故障描述',
    statusProgress: '处理进度',
    overview: '概览',
    history: '流转记录',
    media: '附件',
    transitionRemark: '流转备注',
    approve: '进入下一步',
    reject: '退回上一步',
    transitionConfirm: '确认将工单流转至“{status}”？',
    deletePartConfirm: '确认从工单中移除该备件？',
    warranty: '质保',
    warrantyPending: '待判定',
    warrantyYes: '质保',
    warrantyNo: '非质保',
    noParts: '尚未添加备件',
    noMedia: '尚未上传附件',
    noHistory: '暂无流转记录',
    archivedReadonly: '已归档工单仅供查看',
  },
  status: {
    draft: '草稿',
    fault_report: '故障提报',
    initial_review: '初审',
    technical_eval: '技术评估',
    repair_execution: '维修执行',
    archived: '已归档',
    pending: '待提交',
    delivered: '待确认',
    confirmed: '已确认',
  },
  priority: {
    low: '低',
    normal: '普通',
    high: '高',
    urgent: '紧急',
  },
  fault: {
    engine: '发动机',
    transmission: '变速箱',
    electrical: '电气系统',
    ac: '空调系统',
    brake: '制动系统',
    suspension: '悬挂系统',
    body: '车身',
    other: '其他',
  },
  parts: {
    number: '料号',
    name: '名称',
    image: '图片',
    quantity: '数量',
    ordered: '订单数量',
    received: '实到数量',
    noImage: '无图片',
  },
  inventoryOrder: {
    title: '备件订单',
    subtitle: '跟踪经销商补货、到货与确认入库',
    create: '创建备件订单',
    createSubtitle: '选择仓库并添加本次补货料品',
    id: '订单编号',
    warehouse: '经销商仓库',
    items: '料品数',
    createdBy: '创建人',
    noOrders: '暂无备件订单',
    noOrdersHint: '创建一张备件订单开始补货。',
    detail: '备件订单详情',
    deliver: '提交实际数量',
    confirmInbound: '确认入库',
    confirmInboundText: '确认后，实到数量将计入经销商库存。此操作不可撤销。',
    deliverSuccess: '实际到货数量已提交',
    confirmSuccess: '备件已确认入库',
    chooseWarehouse: '请选择仓库',
    addRow: '添加一行',
    importCsv: '导入 CSV',
    invalidItems: '请选择仓库并至少添加一个有效料品',
    createSuccess: '备件订单创建成功',
  },
  warehouse: {
    title: '备件库存',
    subtitle: '查看各仓库库存余量和变更记录',
    create: '新建仓库',
    edit: '编辑仓库',
    name: '仓库名称',
    owner: '所属经销商',
    selectOwner: '请选择经销商',
    inventory: '库存清单',
    logs: '操作记录',
    noWarehouse: '暂无仓库',
    noWarehouseHint: '创建仓库后即可管理备件库存。',
    noInventory: '该仓库暂无库存',
    noLogs: '暂无库存变更记录',
    stock: '库存',
    stockNormal: '库存充足',
    stockLow: '库存偏低',
    stockCritical: '库存紧张',
    type: '类型',
    operator: '操作人',
    inbound: '入库',
    outbound: '出库',
    order: '关联订单',
    created: '仓库创建成功',
    updated: '仓库信息已更新',
  },
  account: {
    title: '账号与用户',
    subtitle: '维护个人资料、安全设置和系统用户',
    profile: '个人资料',
    displayName: '显示名称',
    email: '邮箱',
    language: '默认语言',
    profileSaved: '个人资料已保存',
    security: '安全设置',
    newPassword: '新密码',
    passwordHint: '至少 6 个字符',
    passwordSaved: '密码已更新',
    users: '用户管理',
    addUser: '新增用户',
    editUser: '编辑用户',
    username: '用户名',
    role: '角色',
    dealer: '上级经销商',
    active: '启用账号',
    passwordOptional: '新密码（留空则不修改）',
    userCreated: '用户已创建',
    userUpdated: '用户已更新',
    requiredUser: '用户名和密码为必填项',
  },
  process: {
    title: '工单配置',
    subtitle: '维护工单流程步骤与故障类型',
    processTab: '流程步骤',
    faultTab: '故障类型',
    addStep: '新增步骤',
    editStep: '编辑步骤',
    saveOrder: '保存顺序',
    moveUp: '上移',
    moveDown: '下移',
    deleteStep: '删除步骤',
    deleteConfirm: '确认删除该流程步骤？',
    anchor: '固定步骤',
    saveAll: '保存更改',
    step: '步骤 {number}',
    handler: '处理角色',
    enabled: '启用步骤',
    labels: '多语言名称',
    fields: '字段规则',
    requiredFields: '必填字段',
    editableFields: '可编辑字段',
    notifyRoles: '通知角色',
    commaHint: '多个值使用英文逗号分隔',
    saved: '流程设置已保存',
    created: '流程步骤已创建',
    deleted: '流程步骤已删除',
    orderSaved: '流程顺序已保存',
    noSteps: '暂无流程步骤',
  },
  faultConfig: {
    title: '故障类型',
    subtitle: '维护新建工单可选择的故障分类',
    add: '新增故障类型',
    edit: '编辑故障类型',
    code: '稳定代码',
    codeHint: '仅限小写字母、数字和下划线，创建后不可修改',
    chinese: '中文名称',
    english: '英文名称',
    portuguese: '葡萄牙语名称',
    enabled: '启用类型',
    saveOrder: '保存顺序',
    deleteConfirm: '确认删除该故障类型？',
    created: '故障类型已创建',
    updated: '故障类型已更新',
    deleted: '故障类型已删除',
    orderSaved: '故障类型顺序已保存',
    noTypes: '暂无故障类型',
    atLeastOne: '至少需要保留一个启用的故障类型',
  },
  data: {
    title: '资料管理',
    subtitle: '批量维护工单、料品和 VIN 基础资料',
    orders: '工单',
    parts: '料品',
    vins: 'VIN',
    importOrders: '导入工单',
    importParts: '导入料品',
    importVins: '导入 VIN',
    csvOrPaste: 'CSV 文件或粘贴数据',
    imageMatch: '批量匹配料品图片',
    imageHint: '文件名必须与料号一致，例如 MB-X100.jpg',
    filter: '筛选当前列表',
    selected: '已选择 {count} 项',
    deleteSelected: '删除所选',
    deleteConfirm: '确认删除所选的 {count} 项？此操作不可撤销。',
    importSuccess: '成功 {success} 条，失败 {failed} 条',
    matched: '匹配成功 {matched} 张，未匹配 {unmatched} 张',
    duplicate: '重复数据将自动跳过',
    invalidData: '请选择文件或输入有效数据',
  },
}

const en: typeof zh = {
  app: { name: 'After-Sales Management', shortName: 'After-Sales', tagline: 'Orders, parts and inventory in one workspace', loading: 'Loading', save: 'Save', saving: 'Saving…', cancel: 'Cancel', confirm: 'Confirm', close: 'Close', edit: 'Edit', delete: 'Delete', back: 'Back', search: 'Search', reset: 'Reset', add: 'Add', create: 'Create', submit: 'Submit', view: 'View', actions: 'Actions', all: 'All', none: 'None', total: '{count} items', page: 'Page {page} of {pages}', previous: 'Previous', next: 'Next', required: 'Required', optional: 'Optional', success: 'Done', error: 'Something went wrong', networkError: 'Network connection failed. Please try again.', unsaved: 'You have unsaved changes', upload: 'Upload file', downloadTemplate: 'Download template', selectFile: 'Choose file', dragFile: 'Drop files here, or click to browse', noResults: 'No matching results', clearSelection: 'Clear selection' },
  nav: { orders: 'Orders', orderList: 'Order list', createOrder: 'Create order', partsOrders: 'Parts orders', warehouse: 'Parts inventory', account: 'Account & users', process: 'Order configuration', data: 'Data management', collapse: 'Collapse sidebar', expand: 'Expand sidebar', openMenu: 'Open navigation', logout: 'Sign out' },
  auth: { title: 'Welcome back', subtitle: 'Sign in to manage service orders and parts', username: 'Username', password: 'Password', usernamePlaceholder: 'Enter username', passwordPlaceholder: 'Enter password', signIn: 'Sign in', signingIn: 'Signing in…', required: 'Enter your username and password', failed: 'Incorrect username or password' },
  role: { admin: 'Administrator', repair_station: 'Repair station', brazil: 'Brazil service', naitteng: 'Naitteng' },
  order: { listTitle: 'Service orders', listSubtitle: 'Review, filter and follow all accessible orders', createTitle: 'Create service order', createSubtitle: 'Record vehicle, fault, media and required parts', detailTitle: 'Order details', new: 'New order', keyword: 'Order number, VIN or title', status: 'Status', priority: 'Priority', orderNumber: 'Order number', vin: 'VIN', title: 'Order title', faultType: 'Fault type', creator: 'Created by', createdAt: 'Created', noOrders: 'No service orders yet', noOrdersHint: 'Create the first order to start tracking after-sales work.', basicInfo: 'Basic information', mileage: 'Mileage', mileagePlaceholder: 'e.g. 12,000 km', faultInfo: 'Fault information', chooseFault: 'Choose a fault type', description: 'Fault description', descriptionPlaceholder: 'Describe the symptoms, conditions and checks performed', remark: 'Notes', remarkPlaceholder: 'Additional notes (optional)', attachments: 'Photos and videos', attachmentsHint: 'Images and videos are supported; keep individual files reasonably sized', parts: 'Parts', addPart: 'Add part', partSearch: 'Search by number or name', quantity: 'Quantity', createSuccess: 'Order {number} created', createFailed: 'Could not create order', requiredHint: 'Enter an order title and fault description', statusProgress: 'Progress', overview: 'Overview', history: 'History', media: 'Attachments', transitionRemark: 'Transition note', approve: 'Move forward', reject: 'Send back', transitionConfirm: 'Move this order to “{status}”?', deletePartConfirm: 'Remove this part from the order?', warranty: 'Warranty', warrantyPending: 'Pending', warrantyYes: 'Warranty', warrantyNo: 'Not covered', noParts: 'No parts added', noMedia: 'No attachments uploaded', noHistory: 'No transition history', archivedReadonly: 'Archived orders are read-only' },
  status: { draft: 'Draft', fault_report: 'Fault report', initial_review: 'Initial review', technical_eval: 'Technical evaluation', repair_execution: 'Repair execution', archived: 'Archived', pending: 'Pending', delivered: 'Awaiting confirmation', confirmed: 'Confirmed' },
  priority: { low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent' },
  fault: { engine: 'Engine', transmission: 'Transmission', electrical: 'Electrical', ac: 'Air conditioning', brake: 'Brakes', suspension: 'Suspension', body: 'Body', other: 'Other' },
  parts: { number: 'Part number', name: 'Name', image: 'Image', quantity: 'Quantity', ordered: 'Ordered', received: 'Received', noImage: 'No image' },
  inventoryOrder: { title: 'Parts orders', subtitle: 'Track replenishment, delivery and warehouse confirmation', create: 'Create parts order', createSubtitle: 'Choose a warehouse and add replenishment items', id: 'Order ID', warehouse: 'Dealer warehouse', items: 'Items', createdBy: 'Created by', noOrders: 'No parts orders', noOrdersHint: 'Create a parts order to start replenishment.', detail: 'Parts order details', deliver: 'Submit received quantities', confirmInbound: 'Confirm inbound', confirmInboundText: 'Received quantities will be added to dealer inventory. This cannot be undone.', deliverSuccess: 'Received quantities submitted', confirmSuccess: 'Parts confirmed into inventory', chooseWarehouse: 'Choose a warehouse', addRow: 'Add row', importCsv: 'Import CSV', invalidItems: 'Choose a warehouse and at least one valid part', createSuccess: 'Parts order created' },
  warehouse: { title: 'Parts inventory', subtitle: 'Review stock levels and warehouse activity', create: 'Create warehouse', edit: 'Edit warehouse', name: 'Warehouse name', owner: 'Dealer owner', selectOwner: 'Choose a dealer', inventory: 'Inventory', logs: 'Activity', noWarehouse: 'No warehouses', noWarehouseHint: 'Create a warehouse to start managing inventory.', noInventory: 'No stock in this warehouse', noLogs: 'No inventory activity', stock: 'Stock', stockNormal: 'In stock', stockLow: 'Low stock', stockCritical: 'Critical stock', type: 'Type', operator: 'Operator', inbound: 'Inbound', outbound: 'Outbound', order: 'Related order', created: 'Warehouse created', updated: 'Warehouse updated' },
  account: { title: 'Account & users', subtitle: 'Manage your profile, security and system users', profile: 'Profile', displayName: 'Display name', email: 'Email', language: 'Default language', profileSaved: 'Profile saved', security: 'Security', newPassword: 'New password', passwordHint: 'At least 6 characters', passwordSaved: 'Password updated', users: 'User management', addUser: 'Add user', editUser: 'Edit user', username: 'Username', role: 'Role', dealer: 'Parent dealer', active: 'Account enabled', passwordOptional: 'New password (leave blank to keep current)', userCreated: 'User created', userUpdated: 'User updated', requiredUser: 'Username and password are required' },
  process: { title: 'Order configuration', subtitle: 'Manage workflow steps and fault types', processTab: 'Process steps', faultTab: 'Fault types', addStep: 'Add step', editStep: 'Edit step', saveOrder: 'Save order', moveUp: 'Move up', moveDown: 'Move down', deleteStep: 'Delete step', deleteConfirm: 'Delete this process step?', anchor: 'Fixed step', saveAll: 'Save changes', step: 'Step {number}', handler: 'Handler role', enabled: 'Step enabled', labels: 'Multilingual labels', fields: 'Field rules', requiredFields: 'Required fields', editableFields: 'Editable fields', notifyRoles: 'Notify roles', commaHint: 'Separate multiple values with commas', saved: 'Process settings saved', created: 'Process step created', deleted: 'Process step deleted', orderSaved: 'Process order saved', noSteps: 'No process steps' },
  faultConfig: { title: 'Fault types', subtitle: 'Manage the fault categories available on new orders', add: 'Add fault type', edit: 'Edit fault type', code: 'Stable code', codeHint: 'Lowercase letters, numbers and underscores only; cannot be changed after creation', chinese: 'Chinese name', english: 'English name', portuguese: 'Portuguese name', enabled: 'Type enabled', saveOrder: 'Save order', deleteConfirm: 'Delete this fault type?', created: 'Fault type created', updated: 'Fault type updated', deleted: 'Fault type deleted', orderSaved: 'Fault type order saved', noTypes: 'No fault types', atLeastOne: 'At least one fault type must remain active' },
  data: { title: 'Data management', subtitle: 'Maintain orders, parts and VIN reference data in bulk', orders: 'Orders', parts: 'Parts', vins: 'VINs', importOrders: 'Import orders', importParts: 'Import parts', importVins: 'Import VINs', csvOrPaste: 'CSV file or pasted data', imageMatch: 'Match part images', imageHint: 'The filename must match the part number, e.g. MB-X100.jpg', filter: 'Filter this list', selected: '{count} selected', deleteSelected: 'Delete selected', deleteConfirm: 'Delete {count} selected items? This cannot be undone.', importSuccess: '{success} succeeded, {failed} failed', matched: '{matched} matched, {unmatched} unmatched', duplicate: 'Duplicates will be skipped automatically', invalidData: 'Choose a file or enter valid data' },
}

const pt: typeof zh = {
  app: { name: 'Gestão de Pós-Venda', shortName: 'Pós-Venda', tagline: 'Ordens, peças e estoque em um só lugar', loading: 'Carregando', save: 'Salvar', saving: 'Salvando…', cancel: 'Cancelar', confirm: 'Confirmar', close: 'Fechar', edit: 'Editar', delete: 'Excluir', back: 'Voltar', search: 'Pesquisar', reset: 'Limpar', add: 'Adicionar', create: 'Criar', submit: 'Enviar', view: 'Ver', actions: 'Ações', all: 'Todos', none: 'Nenhum', total: '{count} itens', page: 'Página {page} de {pages}', previous: 'Anterior', next: 'Próxima', required: 'Obrigatório', optional: 'Opcional', success: 'Concluído', error: 'Ocorreu um erro', networkError: 'Falha de conexão. Tente novamente.', unsaved: 'Existem alterações não salvas', upload: 'Enviar arquivo', downloadTemplate: 'Baixar modelo', selectFile: 'Escolher arquivo', dragFile: 'Arraste arquivos aqui ou clique para escolher', noResults: 'Nenhum resultado encontrado', clearSelection: 'Limpar seleção' },
  nav: { orders: 'Ordens', orderList: 'Lista de ordens', createOrder: 'Criar ordem', partsOrders: 'Pedidos de peças', warehouse: 'Estoque de peças', account: 'Conta e usuários', process: 'Configuração de ordens', data: 'Gestão de dados', collapse: 'Recolher menu', expand: 'Expandir menu', openMenu: 'Abrir navegação', logout: 'Sair' },
  auth: { title: 'Bem-vindo de volta', subtitle: 'Entre para gerenciar ordens e peças', username: 'Usuário', password: 'Senha', usernamePlaceholder: 'Digite o usuário', passwordPlaceholder: 'Digite a senha', signIn: 'Entrar', signingIn: 'Entrando…', required: 'Digite usuário e senha', failed: 'Usuário ou senha incorretos' },
  role: { admin: 'Administrador', repair_station: 'Oficina', brazil: 'Serviço Brasil', naitteng: 'Naitteng' },
  order: { listTitle: 'Ordens de serviço', listSubtitle: 'Consulte, filtre e acompanhe as ordens disponíveis', createTitle: 'Criar ordem de serviço', createSubtitle: 'Registre veículo, falha, mídia e peças necessárias', detailTitle: 'Detalhes da ordem', new: 'Nova ordem', keyword: 'Número, VIN ou título', status: 'Status', priority: 'Prioridade', orderNumber: 'Número da ordem', vin: 'VIN', title: 'Título da ordem', faultType: 'Tipo de falha', creator: 'Criado por', createdAt: 'Criado em', noOrders: 'Nenhuma ordem de serviço', noOrdersHint: 'Crie a primeira ordem para começar o atendimento.', basicInfo: 'Informações básicas', mileage: 'Quilometragem', mileagePlaceholder: 'Ex.: 12.000 km', faultInfo: 'Informações da falha', chooseFault: 'Escolha o tipo de falha', description: 'Descrição da falha', descriptionPlaceholder: 'Descreva sintomas, condições e verificações realizadas', remark: 'Observações', remarkPlaceholder: 'Observações adicionais (opcional)', attachments: 'Fotos e vídeos', attachmentsHint: 'Imagens e vídeos são aceitos; use arquivos de tamanho razoável', parts: 'Peças', addPart: 'Adicionar peça', partSearch: 'Pesquisar por código ou nome', quantity: 'Quantidade', createSuccess: 'Ordem {number} criada', createFailed: 'Não foi possível criar a ordem', requiredHint: 'Preencha o título e a descrição da falha', statusProgress: 'Progresso', overview: 'Visão geral', history: 'Histórico', media: 'Anexos', transitionRemark: 'Observação da transição', approve: 'Avançar', reject: 'Devolver', transitionConfirm: 'Mover esta ordem para “{status}”?', deletePartConfirm: 'Remover esta peça da ordem?', warranty: 'Garantia', warrantyPending: 'Pendente', warrantyYes: 'Garantia', warrantyNo: 'Fora da garantia', noParts: 'Nenhuma peça adicionada', noMedia: 'Nenhum anexo enviado', noHistory: 'Sem histórico de transição', archivedReadonly: 'Ordens arquivadas são somente leitura' },
  status: { draft: 'Rascunho', fault_report: 'Relato de falha', initial_review: 'Revisão inicial', technical_eval: 'Avaliação técnica', repair_execution: 'Execução do reparo', archived: 'Arquivado', pending: 'Pendente', delivered: 'Aguardando confirmação', confirmed: 'Confirmado' },
  priority: { low: 'Baixa', normal: 'Normal', high: 'Alta', urgent: 'Urgente' },
  fault: { engine: 'Motor', transmission: 'Transmissão', electrical: 'Elétrica', ac: 'Ar-condicionado', brake: 'Freios', suspension: 'Suspensão', body: 'Carroceria', other: 'Outros' },
  parts: { number: 'Código', name: 'Nome', image: 'Imagem', quantity: 'Quantidade', ordered: 'Pedido', received: 'Recebido', noImage: 'Sem imagem' },
  inventoryOrder: { title: 'Pedidos de peças', subtitle: 'Acompanhe reposição, entrega e entrada no estoque', create: 'Criar pedido de peças', createSubtitle: 'Escolha o depósito e adicione os itens de reposição', id: 'ID do pedido', warehouse: 'Depósito do revendedor', items: 'Itens', createdBy: 'Criado por', noOrders: 'Nenhum pedido de peças', noOrdersHint: 'Crie um pedido para iniciar a reposição.', detail: 'Detalhes do pedido', deliver: 'Enviar quantidades recebidas', confirmInbound: 'Confirmar entrada', confirmInboundText: 'As quantidades recebidas serão adicionadas ao estoque. Esta ação não pode ser desfeita.', deliverSuccess: 'Quantidades recebidas enviadas', confirmSuccess: 'Entrada de peças confirmada', chooseWarehouse: 'Escolha um depósito', addRow: 'Adicionar linha', importCsv: 'Importar CSV', invalidItems: 'Escolha um depósito e ao menos uma peça válida', createSuccess: 'Pedido de peças criado' },
  warehouse: { title: 'Estoque de peças', subtitle: 'Consulte saldos e movimentações dos depósitos', create: 'Criar depósito', edit: 'Editar depósito', name: 'Nome do depósito', owner: 'Revendedor responsável', selectOwner: 'Escolha um revendedor', inventory: 'Estoque', logs: 'Movimentações', noWarehouse: 'Nenhum depósito', noWarehouseHint: 'Crie um depósito para começar a controlar o estoque.', noInventory: 'Sem estoque neste depósito', noLogs: 'Sem movimentações', stock: 'Estoque', stockNormal: 'Disponível', stockLow: 'Estoque baixo', stockCritical: 'Estoque crítico', type: 'Tipo', operator: 'Operador', inbound: 'Entrada', outbound: 'Saída', order: 'Pedido relacionado', created: 'Depósito criado', updated: 'Depósito atualizado' },
  account: { title: 'Conta e usuários', subtitle: 'Gerencie seu perfil, segurança e usuários do sistema', profile: 'Perfil', displayName: 'Nome de exibição', email: 'E-mail', language: 'Idioma padrão', profileSaved: 'Perfil salvo', security: 'Segurança', newPassword: 'Nova senha', passwordHint: 'Pelo menos 6 caracteres', passwordSaved: 'Senha atualizada', users: 'Gestão de usuários', addUser: 'Adicionar usuário', editUser: 'Editar usuário', username: 'Usuário', role: 'Função', dealer: 'Revendedor superior', active: 'Conta ativa', passwordOptional: 'Nova senha (deixe em branco para manter)', userCreated: 'Usuário criado', userUpdated: 'Usuário atualizado', requiredUser: 'Usuário e senha são obrigatórios' },
  process: { title: 'Configuração de ordens', subtitle: 'Gerencie etapas do fluxo e tipos de falha', processTab: 'Etapas do fluxo', faultTab: 'Tipos de falha', addStep: 'Adicionar etapa', editStep: 'Editar etapa', saveOrder: 'Salvar ordem', moveUp: 'Mover para cima', moveDown: 'Mover para baixo', deleteStep: 'Excluir etapa', deleteConfirm: 'Excluir esta etapa do fluxo?', anchor: 'Etapa fixa', saveAll: 'Salvar alterações', step: 'Etapa {number}', handler: 'Responsável', enabled: 'Etapa ativa', labels: 'Nomes multilíngues', fields: 'Regras de campos', requiredFields: 'Campos obrigatórios', editableFields: 'Campos editáveis', notifyRoles: 'Funções notificadas', commaHint: 'Separe vários valores por vírgulas', saved: 'Configuração salva', created: 'Etapa criada', deleted: 'Etapa excluída', orderSaved: 'Ordem do fluxo salva', noSteps: 'Nenhuma etapa configurada' },
  faultConfig: { title: 'Tipos de falha', subtitle: 'Gerencie as categorias disponíveis em novas ordens', add: 'Adicionar tipo', edit: 'Editar tipo', code: 'Código estável', codeHint: 'Use apenas letras minúsculas, números e sublinhados; não pode ser alterado depois', chinese: 'Nome em chinês', english: 'Nome em inglês', portuguese: 'Nome em português', enabled: 'Tipo ativo', saveOrder: 'Salvar ordem', deleteConfirm: 'Excluir este tipo de falha?', created: 'Tipo de falha criado', updated: 'Tipo de falha atualizado', deleted: 'Tipo de falha excluído', orderSaved: 'Ordem dos tipos salva', noTypes: 'Nenhum tipo de falha', atLeastOne: 'Pelo menos um tipo de falha deve permanecer ativo' },
  data: { title: 'Gestão de dados', subtitle: 'Mantenha ordens, peças e VINs em lote', orders: 'Ordens', parts: 'Peças', vins: 'VINs', importOrders: 'Importar ordens', importParts: 'Importar peças', importVins: 'Importar VINs', csvOrPaste: 'Arquivo CSV ou dados colados', imageMatch: 'Associar imagens de peças', imageHint: 'O nome do arquivo deve ser igual ao código, ex.: MB-X100.jpg', filter: 'Filtrar lista', selected: '{count} selecionados', deleteSelected: 'Excluir selecionados', deleteConfirm: 'Excluir {count} itens selecionados? Esta ação não pode ser desfeita.', importSuccess: '{success} importados, {failed} falharam', matched: '{matched} associados, {unmatched} não associados', duplicate: 'Duplicados serão ignorados automaticamente', invalidData: 'Escolha um arquivo ou informe dados válidos' },
}

const initial = (localStorage.getItem('locale') || 'zh') as Language

const i18n = createI18n({
  legacy: false,
  locale: ['zh', 'en', 'pt'].includes(initial) ? initial : 'zh',
  fallbackLocale: 'zh',
  messages: { zh, en, pt },
})

export const messages = { zh, en, pt }
export default i18n
